import html2canvas from "html2canvas-pro";
import {DateTime} from "luxon";
import {nCols, nRows} from "~/components/reports/reportConstants";
import type {ChartSeriesDetails, ComboChartSeriesDetails} from "~/components/reports/widgetSpecific/comboChart";
import {widgetTypeDetails} from "~/components/reports/widgetSpecific/maps/widgetTypeDetails";
import {widgetTypeToCleanWidgetForDatabaseSync} from "~/components/reports/widgetSpecific/maps/widgetTypeToCleanWidgetForDatabaseSync";
import {createDefaultTableComparisonConfig} from "~/domainModels/reports/tableComparison";
import type {EditableReportPieChartData} from "~/components/reports/widgetSpecific/types";
import {ComposedChartType} from "~/components/reports/widgetSpecific/types";
import type {EditableWidget, Widget} from "~/components/reports/widgetSpecific/widgets";
import {WidgetType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import type {ReportPage} from "~/domainModels/reports/reportPage";
import type {EditableReportAction} from "~/modules/reports/state/editableReportState";
import {EditableReportActionType} from "~/modules/reports/state/editableReportState";
import type {
    ColumnProperties,
    DndGridOccupancyMatrix,
    EditableReportPage,
    WidgetSorts,
} from "~/modules/reports/domain/reportTypes";
import {
    ColumnSortType,
    DateRangeOptions,
    FilterOperations,
    TableColumnDataTypes,
    TableColumnTextOverflow,
    WidgetValueType,
} from "~/modules/reports/domain/reportTypes";
import {CanvasControlType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getUuidFromUnknown} from "@vesta/global-common-typescript/utilities/typeValidationUtilities";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {iso8601DateTimeValidationPattern} from "@vesta/global-common-typescript/utilities/validationPatterns";
import type {AtomicFilter} from "~/typeDefinitions";
import {BackendFilterOperations, BackendFilterValueTypes} from "~/typeDefinitions";
import {showSuccessToast} from "~/utilities/utilities";
import type {CanvasControl, CanvasControls} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";

export function getDateRangeForCanvasControl(
    canvasControl: CanvasControl | undefined,
    timezone: string,
    {useDefaultValue = true}: {useDefaultValue?: boolean} = {},
): {startDateIso: Iso8601DateTime; endDateIso: Iso8601DateTime} | null {
    if (canvasControl?.type !== CanvasControlType.dateRange) {
        return null;
    }

    if (useDefaultValue === false && (canvasControl.value[0] === null || canvasControl.value[1] === null)) {
        return null;
    }

    const dateRange = useDefaultValue ? (canvasControl.defaultDateRange ?? canvasControl.dateRange) : canvasControl.dateRange;
    if (dateRange !== null) {
        const {startDate, endDate} = getDatesForDateRangeOption(dateRange, timezone);
        return {
            startDateIso: startDate,
            endDateIso: endDate,
        };
    }

    const [startDateIso, endDateIso] = useDefaultValue ? canvasControl.defaultValue : canvasControl.value;
    if (startDateIso === null || endDateIso === null) {
        return null;
    }

    return {
        startDateIso,
        endDateIso,
    };
}

export function widgetToEditableWidget(widget: Widget, timezone: string, canvasControls?: CanvasControls): EditableWidget {
    let editableWidget: EditableWidget = {
        ...widget,
        rowStart: widget.row,
        colStart: widget.col,
        rowEnd: widget.row + widget.rowSpan,
        colEnd: widget.col + widget.colSpan,
    };

    const canvasDateRange =
        widget.dateRangeCanvasControl === null || widget.dateRangeCanvasControl === undefined
            ? null
            : getDateRangeForCanvasControl(canvasControls?.[widget.dateRangeCanvasControl], timezone);

    if (canvasDateRange !== null) {
        editableWidget = {
            ...editableWidget,
            startDateIso: canvasDateRange.startDateIso,
            endDateIso: canvasDateRange.endDateIso,
            startDateJs: DateTime.fromISO(canvasDateRange.startDateIso).toJSDate(),
            endDateJs: DateTime.fromISO(canvasDateRange.endDateIso).toJSDate(),
        };
    } else if (widget.dateRange != null) {
        const {startDate, endDate} = getDatesForDateRangeOption(widget.dateRange, timezone);
        editableWidget = {
            ...editableWidget,
            startDateIso: startDate,
            endDateIso: endDate,
            startDateJs: DateTime.fromISO(startDate).toJSDate(),
            endDateJs: DateTime.fromISO(endDate).toJSDate(),
        };
    } else if (widget.startDateIso != null && widget.endDateIso != null) {
        editableWidget = {
            ...editableWidget,
            startDateIso: widget.startDateIso,
            endDateIso: widget.endDateIso,
            startDateJs: DateTime.fromISO(widget.startDateIso).toJSDate(),
            endDateJs: DateTime.fromISO(widget.endDateIso).toJSDate(),
        };
    } else if (widget.startDateIso == null && widget.endDateIso == null) {
        const startDate = DateTime.now().minus({month: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
        const endDate = DateTime.now().setZone(timezone).toUTC().toISO() as Iso8601DateTime;

        editableWidget = {
            ...editableWidget,
            startDateIso: startDate,
            endDateIso: endDate,
            startDateJs: DateTime.fromISO(startDate).toJSDate(),
            endDateJs: DateTime.fromISO(endDate).toJSDate(),
        };
    }

    if (editableWidget.type === WidgetType.table) {
        editableWidget.comparison = editableWidget.comparison ?? createDefaultTableComparisonConfig();

        if (editableWidget.dateRange != null || editableWidget.dateRangeCanvasControl != null) {
            editableWidget.comparison = {
                ...editableWidget.comparison,
                ...getComparisonDateRangeForPrimaryRange(editableWidget.startDateIso, editableWidget.endDateIso, timezone),
            };
        }
    }

    delete editableWidget["row"];
    delete editableWidget["rowSpan"];
    delete editableWidget["col"];
    delete editableWidget["colSpan"];

    return editableWidget;
}

export function editableWidgetToWidget(editableWidget: EditableWidget): Widget {
    // TODO: Figure how to do this in a better fashion later
    const widget = {
        ...editableWidget,
        row: editableWidget.rowStart,
        rowSpan: editableWidget.rowEnd - editableWidget.rowStart,
        col: editableWidget.colStart,
        colSpan: editableWidget.colEnd - editableWidget.colStart,
    };
    delete widget.rowStart;
    delete widget.rowEnd;
    delete widget.colStart;
    delete widget.colEnd;
    // TODO: Delete extra properties if present. For instance, a barGraph might have
    // properties from text because of changes during editing phase.
    return widget;
}

// TODO: Shift to utilities later
export function reportPageToEditableReportPage(page: ReportPage, timezone: string, canvasControls?: CanvasControls): EditableReportPage {
    const gridCellToOccupancy: DndGridOccupancyMatrix = Array.from({length: nRows}, () => Array(nCols).fill(null));

    const editableWidgets: Array<EditableWidget> = page.widgets.map((widget) => widgetToEditableWidget(widget, timezone, canvasControls));

    const editableReportPage: EditableReportPage = {
        id: page.id,
        title: page.title,
        widgets: editableWidgets,
        gridCellToOccupancy: gridCellToOccupancy,
        isPageNameEditing: false,
    };

    return editableReportPage;
}

export function editableReportPageToReportPage(editableReportPage: EditableReportPage): ReportPage {
    const widgets: Array<Widget> = editableReportPage.widgets.map((widget) => editableWidgetToWidget(widget));
    const reportPage = {
        ...editableReportPage,
        widgets: widgets,
    };
    delete reportPage.gridCellToOccupancy;
    return reportPage;
}

export function cleanReportPageForDatabaseSync(reportPage: ReportPage): ReportPage {
    return {
        ...reportPage,
        widgets: reportPage.widgets == null ? [] : reportPage.widgets.map((widget) => widgetTypeToCleanWidgetForDatabaseSync[widget.type](widget)),
    };
}

export function cleanCanvasControlsForDatabaseSync(canvasControls: CanvasControls): CanvasControls {
    const finalCanvasControls: {[key: Uuid]: CanvasControl} = {};

    for (const canvasControl of Object.values(canvasControls)) {
        if (canvasControl.type === CanvasControlType.select) {
            const newControl = structuredClone(canvasControl);
            newControl.value = JSON.stringify(newControl.value.split(","));

            finalCanvasControls[newControl.id] = newControl;
            continue;
        }

        finalCanvasControls[canvasControl.id] = canvasControl;
    }

    return finalCanvasControls;
}

export function refreshReportPageIds(reportPage: ReportPage): ReportPage {
    return {
        ...reportPage,
        id: generateUuid(),
        widgets: reportPage.widgets.map((widget) => ({
            ...widget,
            id: generateUuid(),
        })),
    };
}

// export function reportToEditableReport(report: Report): EditableReport {
//     return ;
// }

export function getIntegrationTypeForWidget(widget: Widget): Array<Uuid> {
    switch (widget.type) {
        case WidgetType.table:
        case WidgetType.pieChart:
        case WidgetType.multiBarGraph:
        case WidgetType.stackedBarGraph:
        case WidgetType.lineGraph:
        case WidgetType.areaGraph:
        case WidgetType.numberTile:
        case WidgetType.progressBar:
        case WidgetType.gauge:
        case WidgetType.image:
        case WidgetType.canvasControl:
        case WidgetType.text: {
            return [];
        }
        default: {
            const exhaustiveCheck: never = widget;
            // console.log(`Unexpected action encountered: ${widget}`);
            return [];
        }
    }
}

export function getDatesForDateRangeOption(dateRange: DateRangeOptions, timezone: string): {startDate: Iso8601DateTime; endDate: Iso8601DateTime} {
    switch (dateRange) {
        case DateRangeOptions.today: {
            const startDate = DateTime.now().setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.yesterday: {
            const startDate = DateTime.now().minus({day: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().minus({day: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.thisWeek: {
            const startDate = DateTime.now().startOf("week").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.lastWeek: {
            const startDate = DateTime.now().startOf("week").minus({weeks: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().startOf("week").minus({days: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.lastSevenDays: {
            const startDate = DateTime.now().minus({days: 7}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().minus({days: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.lastFourteenDays: {
            const startDate = DateTime.now().minus({days: 14}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().minus({days: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.monthToDate: {
            const startDate = DateTime.now().startOf("month").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.lastMonth: {
            const startDate = DateTime.now().minus({month: 1}).startOf("month").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().minus({month: 1}).endOf("month").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.lastMonthToDate: {
            const startDate = DateTime.now().minus({month: 1}).startOf("month").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().minus({month: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.lastThirtyDays: {
            const startDate = DateTime.now().minus({days: 30}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().minus({days: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.quarterToDate: {
            const startDate = DateTime.now().startOf("quarter").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.lastQuarter: {
            const startDate = DateTime.now().minus({quarters: 1}).startOf("quarter").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().minus({quarters: 1}).endOf("quarter").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.lastQuarterToDate: {
            const startDate = DateTime.now().startOf("quarter").minus({quarters: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().minus({quarters: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.yearToDate: {
            const startDate = DateTime.now().startOf("year").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.lastYear: {
            const startDate = DateTime.now().minus({year: 1}).startOf("year").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().minus({year: 1}).endOf("year").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        case DateRangeOptions.lastYearToDate: {
            const startDate = DateTime.now().minus({year: 1}).startOf("year").setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            const endDate = DateTime.now().minus({year: 1}).setZone(timezone).toUTC().toISO() as Iso8601DateTime;
            return {
                startDate: startDate,
                endDate: endDate,
            };
        }
        default: {
            throw new Error(`Invalid dateRange ${String(dateRange)}`);
        }
    }
}

export function getComparisonDateRangeForPrimaryRange(
    startDateIso: Iso8601DateTime | null,
    endDateIso: Iso8601DateTime | null,
    timezone: string,
): {startDateIso: Iso8601DateTime | null; endDateIso: Iso8601DateTime | null} {
    if (startDateIso === null || endDateIso === null) {
        return {
            startDateIso: null,
            endDateIso: null,
        };
    }

    const start = DateTime.fromISO(startDateIso, {setZone: true}).setZone(timezone).startOf("day");
    const end = DateTime.fromISO(endDateIso, {setZone: true}).setZone(timezone).startOf("day");
    // `diff` measures the distance between the dates, so add one to include
    // both the primary range's start and end dates.
    const durationInDays = Math.max(Math.round(end.diff(start, "days").days) + 1, 1);
    const comparisonEnd = start.minus({days: 1});
    const comparisonStart = comparisonEnd.minus({days: durationInDays - 1});

    return {
        startDateIso: comparisonStart.toUTC().toISO() as Iso8601DateTime,
        endDateIso: comparisonEnd.toUTC().toISO() as Iso8601DateTime,
    };
}

export const graphsXAxisTickFormatter = (value: string | number) => {
    if (typeof value !== "string") {
        return String(value);
    }
    if (`${value}`.length <= 9) {
        return value;
    }
    let parsedDate = DateTime.fromFormat(value, "yyyy-MM-dd");

    if (parsedDate.invalidReason !== null) {
        const isValueIsoDate = new RegExp(iso8601DateTimeValidationPattern).test(value);
        if (isValueIsoDate === true) {
            parsedDate = DateTime.fromISO(value);
            if (parsedDate.invalidReason !== null) {
                return value;
            }
        } else {
            return value;
        }
    }
    return parsedDate.toFormat("LLL dd, yyyy");
};

export function getDataPointsFromWidgetForWidgetType(widget: Widget) {
    switch (widget.type) {
        case WidgetType.stackedBarGraph:
        case WidgetType.horizontalBarGraph:
        case WidgetType.comboChart:
        case WidgetType.multiBarGraph: {
            return widget.data?.chartData;
        }
        case WidgetType.areaGraph: {
            return widget.data?.chartData;
        }
        case WidgetType.lineGraph: {
            return widget.data?.chartData;
        }
        case WidgetType.donutChart:
        case WidgetType.pieChart: {
            // TODO: Hardcoding pie charts to only filter on segment
            const tempArr = [];

            if (widget.data == null || widget.data.length === 0 || Array.isArray(widget.data) === false) {
                return null;
            }

            const key = widget.data[0]?.key;
            if (key == null) {
                return null;
            }

            for (const dataPoint of widget.data) {
                tempArr.push({
                    [key]: dataPoint.name,
                });
            }

            return tempArr;
        }
        case WidgetType.table: {
            return widget.data;
        }
        case WidgetType.gauge:
        case WidgetType.image:
        case WidgetType.numberTile:
        case WidgetType.progressBar:
        case WidgetType.text: {
            return null;
        }
    }
}

export function filterDataPoint(widget: Widget, dataPoint: {[key: string]: any}) {
    if (widget.type === WidgetType.pieChart || widget.type === WidgetType.rawPieChart) {
        return filterPieChartDataPoint(widget, dataPoint);
    }
    if (widget.filters == null || dataPoint == null) {
        return true;
    }
    let shouldReturn = true;
    let areAllLengthsZero = true;

    for (const [key, operations] of Object.entries(widget.filters)) {
        const values = operations[FilterOperations.equals];
        if (values === null || values === undefined) {
            continue;
        }
        if (values.length > 0) {
            areAllLengthsZero = false;
        }
        if (values.includes(dataPoint[key]) === false && values.length > 0) {
            shouldReturn = false;
        }
    }

    return shouldReturn || areAllLengthsZero;
}

export function sortDataPoints(widget: Widget, columnsToSort: WidgetSorts, dataPoint1: {[key: string]: any}, dataPoint2: {[key: string]: any}) {
    if (columnsToSort == null || Object.keys(columnsToSort).length === 0) {
        return 0;
    }

    // No sorts are applied
    if (Object.values(columnsToSort).every((sort) => sort == null)) {
        return 0;
    }

    for (const [sortKey, sortValue] of Object.entries(columnsToSort)) {
        if (sortValue == null) {
            continue;
        }

        const value1 = dataPoint1[sortKey];
        const value2 = dataPoint2[sortKey];

        if (value1 == null && value2 == null) {
            return 0;
        } else if (value1 == null) {
            return 1;
        } else if (value2 == null) {
            return -1;
        }

        // const columnProperties = widget.columnProperties[sortKey];
        // if (columnProperties.columnDataType === TableColumnDataTypes.string) {
        if (typeof value1 === "string" && typeof value2 === "string") {
            const date1 = DateTime.fromFormat(value1, "dd LLL yyyy");
            const date2 = DateTime.fromFormat(value2, "dd LLL yyyy");

            if (date1.isValid === true && date2.isValid === true) {
                if (sortValue === ColumnSortType.ascending) {
                    return date1.toUnixInteger() - date2.toUnixInteger();
                }

                if (sortValue === ColumnSortType.descending) {
                    return date2.toUnixInteger() - date1.toUnixInteger();
                }
            } else {
                if (sortValue === ColumnSortType.ascending) {
                    return value1.localeCompare(value2);
                }

                if (sortValue === ColumnSortType.descending) {
                    return value2.localeCompare(value1);
                }
            }
        }

        // if (Number.isNaN(parseFloat(value1) === true || Number.isNaN(parseFloat(value2)) === true) {
        //     continue;
        // }

        const parsedValue1 = parseFloat(value1);
        const parsedValue2 = parseFloat(value2);

        if (sortValue === ColumnSortType.ascending) {
            return parsedValue1 - parsedValue2;
        }

        if (sortValue === ColumnSortType.descending) {
            return parsedValue2 - parsedValue1;
        }
    }

    return 0;
}

function filterPieChartDataPoint(widget: Widget, dataPoint: EditableReportPieChartData[0]) {
    if (widget.filters == null || dataPoint == null) {
        return true;
    }
    let shouldReturn = true;
    let areAllLengthsZero = true;

    for (const [key, operations] of Object.entries(widget.filters)) {
        for (const [operation, values] of Object.entries(operations)) {
            if (values.length > 0) {
                areAllLengthsZero = false;
            }
            if (values.includes(`${dataPoint[key]}`) === false && values.length > 0) {
                shouldReturn = false;
            }
        }
    }
    return shouldReturn || areAllLengthsZero;
}

export function createColumnProperties(keys: Array<string>) {
    const columnProperties: {[key: string]: ColumnProperties} = {};

    for (const key of keys) {
        columnProperties[key] = {
            columnDataType: TableColumnDataTypes.string,
            columnTitle: key,
            columnTextOverflow: TableColumnTextOverflow.truncate,
            columnWidth: 100 / keys.length,
            isColumnWidthEdited: false,
        };
    }

    return columnProperties;
}

export function reconcileTableColumnProperties(
    keys: Array<string>,
    existingColumnProperties: Record<string, ColumnProperties> | null | undefined,
): Record<string, ColumnProperties> {
    const defaultColumnProperties = createColumnProperties(keys);
    const reconciledColumnProperties: Record<string, ColumnProperties> = {};

    for (const key of keys) {
        const existingColumnProperty = existingColumnProperties?.[key];
        const defaultColumnProperty = defaultColumnProperties[key];
        if (existingColumnProperty !== undefined) {
            reconciledColumnProperties[key] = existingColumnProperty;
        } else if (defaultColumnProperty !== undefined) {
            reconciledColumnProperties[key] = defaultColumnProperty;
        }
    }

    const existingKeys = Object.keys(existingColumnProperties ?? {});
    const keysChanged = existingKeys.length !== keys.length || keys.some((key) => existingColumnProperties?.[key] === undefined);
    if (keysChanged === false) {
        return reconciledColumnProperties;
    }

    const uneditedKeys = keys.filter((key) => reconciledColumnProperties[key]?.isColumnWidthEdited === false);
    const editedWidth = keys.reduce((total, key) => {
        const column = reconciledColumnProperties[key];
        return column !== undefined && column.isColumnWidthEdited && Number.isFinite(column.columnWidth) && column.columnWidth > 0
            ? total + column.columnWidth
            : total;
    }, 0);

    if (uneditedKeys.length > 0 && editedWidth < 100) {
        const uneditedWidth = (100 - editedWidth) / uneditedKeys.length;
        for (const key of uneditedKeys) {
            const column = reconciledColumnProperties[key];
            if (column === undefined) {
                continue;
            }

            reconciledColumnProperties[key] = {
                ...column,
                columnWidth: uneditedWidth,
            };
        }
    }

    return reconciledColumnProperties;
}

export function getUpdatedCanvasControls(widget: Widget, canvasControls: CanvasControls): Array<string> {
    const result = new Set<string>();

    for (const canvasControl of Object.values(canvasControls)) {
        if (widget.backendFilter != null && Array.isArray(widget.backendFilter.filters) === true) {
            for (const filter of widget.backendFilter.filters) {
                if (filter.valueType !== BackendFilterValueTypes.globalFilter) {
                    continue;
                }

                if (filter.globalFilterId === canvasControl.id) {
                    result.add(
                        `${filter.id}${filter.attribute}${filter.operation}${filter.value ?? filter.values ?? ""}${canvasControl.id}${canvasControl.value}${
                            canvasControl.defaultValue
                        }`,
                    );
                }
            }
        }

        // Checking if granularity is coming from canvas control
        if (widget.granularity != null && widget.granularity.type === WidgetValueType.canvasControl && widget.granularity.value.includes(canvasControl.id)) {
            result.add(`${canvasControl.id}${canvasControl.value}${canvasControl.defaultValue}`);
        }

        // Checking if date range is coming from canvas control
        if (widget.dateRangeCanvasControl != null && widget.dateRangeCanvasControl.includes(canvasControl.id)) {
            result.add(`${canvasControl.id}${canvasControl.value}${canvasControl.defaultValue}`);
        }
    }

    return [...result];
}

export function updateWidgetWhenFilterIsComplete(widget: EditableWidget): string {
    const resultSet = new Set<string>();

    if (widget.backendFilter == null || widget.backendFilter.filters == null) {
        return "";
    }

    // TODO: Typesafety to be fixed after raw widgets are removed, proceeding with forced typesafety for now
    for (const filter of widget.backendFilter.filters) {
        const typedFilter = filter as AtomicFilter;
        if (typedFilter.attribute === null || typedFilter.operation === null || typedFilter.value === null) {
            continue;
        }

        resultSet.add(`${typedFilter.attribute}${typedFilter.operation}${typedFilter.valueType}${typedFilter.value}`);
    }

    return [...resultSet].join(",");
}

export function injectIntegrationTypeInReport(report: ReportWithPermissions): ReportWithPermissionsAndIntegrationTypes {
    return {
        ...report,
        integrationTypes: [...new Set(report.pages?.map((page) => page.widgets.map((widget) => getIntegrationTypeForWidget(widget))).flat(3))],
    };
}

const MAX_CANVAS_DIMENSION = 16000;
const MAX_ELEMENT_DIMENSION = 4000;

async function generateWidgetCanvasWithRef(
    widgetRef: React.MutableRefObject<HTMLDivElement | null>,
    height: string,
    width: string,
    theme: string,
    scale: number = 10,
): Promise<HTMLCanvasElement | null> {
    const element = widgetRef.current;
    if (element == null) {
        console.error("Element is null in ref, e06b7f42-6b76-4573-bea3-ee4d88dc7db5");
        return null;
    }

    element.style.backgroundColor = theme === "dark" ? "#121212" : "#ffffff";

    const clonedElement = element.cloneNode(true) as HTMLElement;
    clonedElement.style.position = "fixed";
    clonedElement.style.right = "100%";
    clonedElement.style.height = height;
    clonedElement.style.width = width;
    clonedElement.style.padding = "24px";

    document.body.append(clonedElement);

    const elementWidth = clonedElement.scrollWidth !== 0 ? clonedElement.scrollWidth : clonedElement.offsetWidth;
    const elementHeight = clonedElement.scrollHeight !== 0 ? clonedElement.scrollHeight : clonedElement.offsetHeight;

    const limitedWidth = Math.min(elementWidth, MAX_ELEMENT_DIMENSION);
    const limitedHeight = Math.min(elementHeight, MAX_ELEMENT_DIMENSION);

    const maxDimension = Math.max(limitedWidth, limitedHeight);
    const safeScale = Math.min(scale, Math.floor(MAX_CANVAS_DIMENSION / maxDimension));
    const finalScale = Math.max(1, safeScale);

    if (elementWidth > MAX_ELEMENT_DIMENSION || elementHeight > MAX_ELEMENT_DIMENSION) {
        clonedElement.style.maxWidth = `${MAX_ELEMENT_DIMENSION}px`;
        clonedElement.style.maxHeight = `${MAX_ELEMENT_DIMENSION}px`;
        clonedElement.style.overflow = "hidden";
    }

    const canvas = await html2canvas(clonedElement, {
        scale: finalScale,
        ignoreElements: (element) => {
            return element.id === "addNewPageButton" || element.id === "should-hide-in-download";
        },
        backgroundColor: theme === "dark" ? "#121212" : "#ffffff",
    });

    element.style.backgroundColor = `transparent`;
    clonedElement.remove();

    return canvas;
}

export async function generateWidgetCanvasWithId(
    id: string,
    height: string,
    width: string,
    theme: string,
    scale: number = 10,
): Promise<HTMLCanvasElement | null> {
    const element = document.getElementById(id);
    if (element === null) {
        console.error(`Element with id ${id} not found, 6881ef5f-5c46-4765-94e2-a4e249a0786c`);
        return null;
    }

    element.style.backgroundColor = theme === "dark" ? "#121212" : "#ffffff";

    const clonedElement = element.cloneNode(true) as HTMLElement;
    clonedElement.style.position = "fixed";
    clonedElement.style.right = "100%";
    clonedElement.style.height = height;
    clonedElement.style.width = width;
    clonedElement.style.padding = "24px";

    document.body.append(clonedElement);

    const elementWidth = clonedElement.scrollWidth !== 0 ? clonedElement.scrollWidth : clonedElement.offsetWidth;
    const elementHeight = clonedElement.scrollHeight !== 0 ? clonedElement.scrollHeight : clonedElement.offsetHeight;

    const limitedWidth = Math.min(elementWidth, MAX_ELEMENT_DIMENSION);
    const limitedHeight = Math.min(elementHeight, MAX_ELEMENT_DIMENSION);

    const maxDimension = Math.max(limitedWidth, limitedHeight);
    const safeScale = Math.min(scale, Math.floor(MAX_CANVAS_DIMENSION / maxDimension));
    const finalScale = Math.max(1, safeScale);

    if (elementWidth > MAX_ELEMENT_DIMENSION || elementHeight > MAX_ELEMENT_DIMENSION) {
        clonedElement.style.maxWidth = `${MAX_ELEMENT_DIMENSION}px`;
        clonedElement.style.maxHeight = `${MAX_ELEMENT_DIMENSION}px`;
        clonedElement.style.overflow = "hidden";
    }

    const canvas = await html2canvas(clonedElement, {
        scale: finalScale,
        ignoreElements: (element) => {
            return element.id === "addNewPageButton" || element.id === "should-hide-in-download";
        },
        backgroundColor: theme === "dark" ? "#121212" : "#ffffff",
    });

    element.style.backgroundColor = `transparent`;
    clonedElement.remove();
    return canvas;
}

export async function downloadWidgetScreenshotWithRef(widgetRef: React.MutableRefObject<HTMLDivElement | null>, height: string, width: string, theme: string) {
    const canvas = await generateWidgetCanvasWithRef(widgetRef, height, width, theme);
    if (canvas == null) {
        return;
    }
    const imgData = canvas.toDataURL("image/jpeg");
    const a = document.createElement("a");
    a.href = imgData;
    a.download = `snapshot.png`;
    a.click();
    showSuccessToast("Success", "Table Downloaded");
}

export async function downloadWidgetScreenshotWithId(id: string, height: string, width: string, theme: string) {
    const canvas = await generateWidgetCanvasWithId(id, height, width, theme);
    if (canvas == null) {
        return;
    }
    const imgData = canvas.toDataURL("image/jpeg");
    const a = document.createElement("a");
    a.href = imgData;
    a.download = `snapshot.png`;
    a.click();
    showSuccessToast("Success", "Table Downloaded");
}

export async function copyWidgetScreenshotToClipboardWithRef(
    widgetRef: React.MutableRefObject<HTMLDivElement | null>,
    height: string,
    width: string,
    theme: string,
) {
    // showInfoToast("1");
    const canvas = await generateWidgetCanvasWithRef(widgetRef, height, width, theme);
    if (canvas == null) {
        // showInfoToast("2");
        return;
    }

    canvas.toBlob((blob) => {
        // showInfoToast("3");
        if (blob == null) {
            return;
        }
        // showInfoToast("4");
        navigator.clipboard.write([new ClipboardItem({"image/jpeg": blob})]);
        // showInfoToast("5");
        showSuccessToast("Success", "Table Copied");
    });
}

export async function copyWidgetScreenshotToClipboardWithId(id: string, height: string, width: string, theme: string) {
    const canvas = await generateWidgetCanvasWithId(id, height, width, theme);
    if (canvas == null) {
        return;
    }

    canvas.toBlob((blob) => {
        if (blob == null) {
            return;
        }
        navigator.clipboard.write([new ClipboardItem({"image/jpeg": blob})]);
        showSuccessToast("Success", "Table Copied");
    });
}

export async function changeWidgetType({
    newWidgetType,
    newWidget,
    currentWidget,
    editableReportDispatch,
    shouldClearMetricsAndDimensions,
}: {
    newWidgetType: WidgetType;
    newWidget: Widget;
    currentWidget: EditableWidget;
    editableReportDispatch: React.Dispatch<EditableReportAction>;
    shouldClearMetricsAndDimensions?: boolean;
}): Promise<void> {
    let persistingObject = {
        isTitleEdited: currentWidget.isTitleEdited,
        title: currentWidget.title,
    };

    if (newWidgetType === WidgetType.numberTile) {
        persistingObject = {
            ...persistingObject,
            datasetId: currentWidget.datasetId ?? null,
            metrics: currentWidget.metrics ?? null,
            dimensions: currentWidget.dimensions ?? null,
            selectedMetrics: currentWidget.selectedMetrics ?? null,
            selectedDimensions: currentWidget.selectedDimensions ?? null,
            minDimensionSelections: null,
            maxDimensionSelections: null,
        };
    } else {
        persistingObject = {
            ...persistingObject,
            datasetId: currentWidget.datasetId ?? null,
            metrics: currentWidget.metrics ?? null,
            dimensions: currentWidget.dimensions ?? null,
            selectedMetrics: currentWidget.selectedMetrics ?? null,
            selectedDimensions: currentWidget.selectedDimensions ?? null,
        };
    }

    if (widgetTypeDetails[newWidget.type].canHaveSecondaryAxis === true && widgetTypeDetails[currentWidget.type].canHaveSecondaryAxis === true) {
        persistingObject = {
            ...persistingObject,
            chartSeriesDetails: currentWidget.chartSeriesDetails,
        };
    }

    if (currentWidget.type === WidgetType.stackedBarGraph && Array.isArray(newWidget.chartSeriesDetails) === true && newWidget.type !== WidgetType.comboChart) {
        // new widget is either bar, line, area
        persistingObject = {
            ...persistingObject,
            chartSeriesDetails: currentWidget.selectedMetrics.map((metric) => ({
                selectedMetric: metric,
                yAxisId: "left",
            })),
            selectedSlicer: currentWidget.selectedSlicer,
        };
    }

    if (currentWidget.type === WidgetType.stackedBarGraph && newWidget.type === WidgetType.comboChart) {
        persistingObject = {
            ...persistingObject,
            chartSeriesDetails: [
                {
                    type: ComposedChartType.bar,
                    selectedMetrics: currentWidget.selectedMetrics,
                    yAxisId: "left",
                },
            ],
            selectedSlicer: currentWidget.selectedSlicer,
        };
    }

    if (
        newWidgetType === WidgetType.comboChart &&
        widgetTypeDetails[currentWidget.type].canHaveSecondaryAxis === true &&
        currentWidget.type !== WidgetType.comboChart &&
        Array.isArray(currentWidget.chartSeriesDetails) === true &&
        currentWidget.chartSeriesDetails.length > 0
    ) {
        const currentChartSeriesDetails: Array<ChartSeriesDetails> = currentWidget.chartSeriesDetails;

        const leftAxisMetrics = currentChartSeriesDetails.filter((series) => series.yAxisId === "left").map((series) => series.selectedMetric);
        const rightAxisMetrics = currentChartSeriesDetails.filter((series) => series.yAxisId === "right").map((series) => series.selectedMetric);

        const newChartSeriesDetails: Array<ComboChartSeriesDetails> = [];
        if (
            currentWidget.type === WidgetType.multiBarGraph ||
            currentWidget.type === WidgetType.stackedBarGraph ||
            currentWidget.type === WidgetType.horizontalBarGraph
        ) {
            if (leftAxisMetrics.length > 0) {
                newChartSeriesDetails.push({
                    type: ComposedChartType.bar,
                    selectedMetrics: leftAxisMetrics,
                    yAxisId: "left",
                });
            }
            if (rightAxisMetrics.length > 0) {
                newChartSeriesDetails.push({
                    type: ComposedChartType.bar,
                    selectedMetrics: rightAxisMetrics,
                    yAxisId: "right",
                });
            }
        }

        if (currentWidget.type === WidgetType.lineGraph) {
            if (leftAxisMetrics.length > 0) {
                newChartSeriesDetails.push({
                    type: ComposedChartType.line,
                    selectedMetrics: leftAxisMetrics,
                    yAxisId: "left",
                });
            }
            if (rightAxisMetrics.length > 0) {
                newChartSeriesDetails.push({
                    type: ComposedChartType.line,
                    selectedMetrics: rightAxisMetrics,
                    yAxisId: "right",
                });
            }
        }

        if (currentWidget.type === WidgetType.areaGraph) {
            if (leftAxisMetrics.length > 0) {
                newChartSeriesDetails.push({
                    type: ComposedChartType.area,
                    selectedMetrics: leftAxisMetrics,
                    yAxisId: "left",
                });
            }
            if (rightAxisMetrics.length > 0) {
                newChartSeriesDetails.push({
                    type: ComposedChartType.area,
                    selectedMetrics: rightAxisMetrics,
                    yAxisId: "right",
                });
            }
        }
        persistingObject = {
            ...persistingObject,
            chartSeriesDetails: newChartSeriesDetails,
        };
    }

    if (
        currentWidget.type === WidgetType.comboChart &&
        widgetTypeDetails[newWidget.type].canHaveSecondaryAxis === true &&
        newWidget.type !== WidgetType.comboChart &&
        Array.isArray(currentWidget.chartSeriesDetails) === true &&
        currentWidget.chartSeriesDetails.length > 0
    ) {
        const currentChartSeriesDetails: Array<ComboChartSeriesDetails> = currentWidget.chartSeriesDetails;

        const newChartSeriesDetails: Array<ChartSeriesDetails> = [];

        const leftAxisMetrics = currentChartSeriesDetails
            .filter((series) => series.yAxisId === "left")
            .map((series) => series.selectedMetrics)
            .flat();
        const rightAxisMetrics = currentChartSeriesDetails
            .filter((series) => series.yAxisId === "right")
            .map((series) => series.selectedMetrics)
            .flat();

        if (leftAxisMetrics.length > 0) {
            newChartSeriesDetails.push(
                ...leftAxisMetrics.map((metric) => ({
                    selectedMetric: metric,
                    yAxisId: "left",
                })),
            );
        }

        if (rightAxisMetrics.length > 0) {
            newChartSeriesDetails.push(
                ...rightAxisMetrics.map((metric) => ({
                    selectedMetric: metric,
                    yAxisId: "right",
                })),
            );
        }

        persistingObject = {
            ...persistingObject,
            chartSeriesDetails: newChartSeriesDetails,
        };
    }

    if (shouldClearMetricsAndDimensions === true) {
        persistingObject = {
            ...persistingObject,
            selectedMetrics: [],
            selectedDimensions: [],
        };
    }

    editableReportDispatch({
        actionType: EditableReportActionType.editWidget,
        widget: currentWidget,
        changedFields: {
            ...currentWidget,
            ...newWidget,
            ...persistingObject,
            startDateIso: currentWidget.startDateIso ?? null,
            endDateIso: currentWidget.endDateIso ?? null,
            startDateJs: currentWidget.startDateJs ?? null,
            endDateJs: currentWidget.endDateJs ?? null,
            dateRange: currentWidget.dateRange ?? null,
            dateRangeCanvasControl: currentWidget.dateRangeCanvasControl ?? null,
            type: newWidgetType,
        },
    });
}

export function commitAtomicFilterTempValue(filter: AtomicFilter): AtomicFilter {
    if (filter.operation === BackendFilterOperations.between) {
        if (filter.value !== null && filter.value !== undefined) {
            return filter;
        }

        if (filter.tempValue !== null && filter.tempValue !== undefined) {
            return {
                ...filter,
                value: filter.tempValue,
            } as AtomicFilter;
        }

        return filter;
    }

    if (filter.operation === BackendFilterOperations.in) {
        return filter;
    }

    if (filter.value !== null && filter.value !== undefined) {
        return filter;
    }

    if (filter.tempValue !== null && filter.tempValue !== undefined) {
        return {
            ...filter,
            value: filter.tempValue,
        } as AtomicFilter;
    }

    return filter;
}

export function checkWhetherFilterIsComplete(filter: AtomicFilter) {
    const committedFilter = commitAtomicFilterTempValue(filter);

    if (committedFilter.attribute === null || committedFilter.operation === null) {
        return false;
    }

    return committedFilter.value !== null && committedFilter.value !== undefined;
}

export function isStaticCanvasControl(canvasControl: CanvasControl): boolean {
    return canvasControl.type === CanvasControlType.granularity || canvasControl.type === CanvasControlType.dateRange;
}

export function getDatasetIdsForCanvasControl(canvasControl: CanvasControl): Array<Uuid> {
    return Object.keys(canvasControl.dabbaToAttributeMapping)
        .map((id) => {
            const parsedId = getUuidFromUnknown(id);
            if (parsedId.success === false) {
                return null;
            }

            return parsedId.data;
        })
        .filter((id) => id !== null);
}
